import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { Brain, Headphones, Sparkles, ArrowRight } from "lucide-react";
import { PhoneVideo } from "../shared/PhoneVideo";

const STEPS = ["Assess", "Access", "Regulate", "Awaken", "Amplify", "Integrate"];
const INCLUDES = [
  { icon: Headphones, label: "Vital Neuro EEG headset — the integration engine" },
  { icon: Brain, label: "Exclusive app + 7 guided pathways (Founding Circle only)" },
  { icon: Sparkles, label: "30–45 day integration journey + monthly live circle" },
];

export const PortalsOfAwe = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const headsetY = useTransform(scrollYProgress, [0, 1], [70, -70]);
  return (
    <section ref={ref} id="portals" data-testid="portals-of-awe" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 hex-texture opacity-60" />
      <div className="absolute -top-20 right-0 w-[45rem] h-[45rem] radial-glow blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-14 items-center">
        {/* Composed product showcase — phone app front & center, headset behind to the left */}
        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="flex justify-center"
        >
          <div className="relative w-full max-w-md mx-auto min-h-[600px] flex items-center justify-center">
            <div className="absolute inset-0 radial-glow-strong blur-3xl pointer-events-none" />
            {/* Headset — transparent cutout, behind & off to the left; scroll parallax + gentle float */}
            <div className="absolute left-[-22%] top-[21%] -translate-y-1/2 w-[80%] z-10 hidden sm:block">
              <motion.div style={{ y: headsetY }}>
                <motion.div
                  animate={{ y: [0, -14, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                >
                  <img src="/assets/portals/vital-neuro-headset-nobg.png" alt="Vital Neuro EEG headset" loading="lazy"
                    className="w-full h-auto block drop-shadow-[0_24px_50px_rgba(0,0,0,0.55)]" />
                </motion.div>
              </motion.div>
            </div>
            {/* Phone — front & center, auto-playing */}
            <div className="relative z-20 drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]">
              <PhoneVideo autoPlay src="/assets/video/poa-app-90s.mp4" poster="/assets/video/poa-app-poster.jpg" label="App Sneak Peek" testid="portals-phone-video" />
            </div>
          </div>
        </motion.div>

        <div>
          <p className="text-sm tracking-[0.24em] uppercase text-[#E7CC86]/80 mb-4">Portals of Awe · Vital Experience</p>
          <h2 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-cream mb-5">
            Not a headset. Not an app. <span className="text-gold">A companion for the moments that matter.</span>
          </h2>
          <p className="text-lg text-cream/70 leading-relaxed mb-8 max-w-xl">
            Gary Malkin's music-as-medicine paired with Vital Neuro's real-time brain-state training — a daily
            practice that supports regulation, presence, and integration precisely when it's needed most.
          </p>

          <div className="flex flex-wrap gap-2 mb-8">
            {STEPS.map((s, i) => (
              <span key={s} className="px-3.5 py-1.5 rounded-full text-sm border border-[#C9A24B]/30 text-cream/75">
                <span className="text-[#E7CC86] mr-1.5">{String(i + 1).padStart(2, "0")}</span>{s}
              </span>
            ))}
          </div>

          <div className="space-y-3 mb-9">
            {INCLUDES.map((it) => (
              <div key={it.label} className="flex items-center gap-3 text-cream/80">
                <it.icon size={20} className="text-[#E7CC86] shrink-0" />
                <span className="text-base">{it.label}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <Link to="/portals-of-awe" data-testid="portals-explore-cta" className="btn-gold px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2">
              Explore the Founding Circle <ArrowRight size={18} />
            </Link>
            <p className="text-sm text-cream/55">$1,497 · limited to 50–100 members</p>
          </div>
        </div>
      </div>
    </section>
  );
};
